import { AxiosResponse, isAxiosError } from 'axios';
import { createLogger } from 'logging';

import type {
    LoginRequest,
    UserSession,
    UserSessionApiDeactivateAllUserSessionsRequest,
    UserSessionApiGetSessionRequest,
    UserSessionApiLogoutRequest,
} from '@videoblocks/user-service-client';

import { userSessionApi } from './userServiceConfig';

const logger = createLogger('LoginApi');

const loginUser = async (loginRequest: LoginRequest): Promise<UserSession | null> => {
    try {
        const result = (await userSessionApi.login({ loginRequest })) as AxiosResponse<UserSession>;
        return result.data;
    } catch (e: unknown) {
        if (isAxiosError(e)) {
            if (e.response?.status === 403) {
                logger.warn('Error logging in. Status code: %s, message: %s', e.status, e.message);
                return null;
            }
            if (e?.response?.status === 404) {
                logger.info('Google Sign On request was valid, but No account found for this email');
                return null;
            }
        }
        throw e;
    }
};

const logoutUser = async (sessionToken: string): Promise<boolean> => {
    const request: UserSessionApiLogoutRequest = {
        sessionId: sessionToken,
    };

    try {
        await userSessionApi.logout(request);
        return true;
    } catch (e: unknown) {
        if (isAxiosError(e)) {
            if (e.response?.status === 403) {
                logger.warn('Error logging out. Status code: %s, message: %s', e.status, e.message);
                return false;
            }
            if (e?.response?.status === 404) {
                logger.info(`No session found for token ${sessionToken}.`);
                return false;
            }
        }
        throw e;
    }
};

const getUserBySession = async (sessionToken: string): Promise<UserSession | null> => {
    const request: UserSessionApiGetSessionRequest = {
        sessionId: sessionToken,
    };
    try {
        const result = (await userSessionApi.getSession(request)) as AxiosResponse<UserSession>;
        return result.data;
    } catch (e: unknown) {
        if (isAxiosError(e)) {
            if (e.response?.status !== 403) {
                logger.warn('Error getting user from session. Status code: %s, message: %s', e.status, e.message);
            }
            if (e.response?.status === 404) {
                return null;
            }
        }
        throw e;
    }
};

const deactivateAllDevices = async (userId: number): Promise<boolean> => {
    const request: UserSessionApiDeactivateAllUserSessionsRequest = {
        userId: userId,
    };
    try {
        const result = (await userSessionApi.deactivateAllUserSessions(request)) as AxiosResponse<unknown>;
        return result.status === 200;
    } catch (e: unknown) {
        if (isAxiosError(e)) {
            if (e.response?.status == 400) {
                logger.warn('Invalid user ID. Status code: %s', e.status);
            } else if (e.response?.status == 404) {
                logger.warn('User not found. Status code: %s', e.status);
            } else {
                logger.warn('Error logging out devices. Status code: %s, message: %s', e.status, e.message);
            }
            return false;
        }
        throw e;
    }
};

export const UserSessionApi = {
    loginUser,
    logoutUser,
    getUserBySession,
    deactivateAllDevices,
};
